import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";

import TrialInstitutionItemIdentityContext from "src/contexts/data/TrialInstitutionItemIdentityContext";
import useTrialInstitutionItemIdentityById from "src/hooks/data/load/useTrialInstitutionItemIdentityById";

/**
 * Loads trial institution item identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteTrialInstitutionItemIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { trialInstitutionItemId } = useParams<{ trialInstitutionItemId: string }>();
    const context = useTrialInstitutionItemIdentityById(
        trialInstitutionItemId ? Number.parseInt(trialInstitutionItemId, 10) : undefined
    );

    return (
        <TrialInstitutionItemIdentityContext.Provider value={context}>
            {children}
        </TrialInstitutionItemIdentityContext.Provider>
    );
};
